import { useMemo, useState } from 'react';
import useSeo from '@/hooks/useSeo';
import { comics, categories } from '@/mocks/comics';

export default function Comics() {
  useSeo({
    title: '漫蛙漫画网漫画库 - 热血、科幻、玄幻、悬疑等连载漫画大全',
    description: '漫蛙漫画网漫画库收录热血、科幻、玄幻、悬疑、恋爱与日常等多种类型的连载漫画，支持高清原图与多种阅读模式，随时随地畅读。',
    canonicalPath: '/comics',
  });
  const [active, setActive] = useState('全部');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    let list = comics;
    if (active !== '全部') {
      list = list.filter((c) => c.category === active);
    }
    if (query.trim()) {
      list = list.filter(
        (c) => c.title.includes(query.trim()) || c.tags.some((t) => t.includes(query.trim())),
      );
    }
    return list;
  }, [active, query]);

  return (
    <div className="bg-background-50">
      {/* Page header */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-24 md:px-6 md:pt-32">
          <nav aria-label="面包屑" className="text-xs text-foreground-500">
            <ol className="flex items-center gap-1.5">
              <li><a href="/" className="hover:text-primary-600">首页</a></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-700">漫画库</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">漫画库</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            精选热血、科幻、玄幻、悬疑、恋爱与日常等多种类型的连载漫画，支持高清原图与多种阅读模式，任你挑选。
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur md:top-20">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === cat
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'border border-background-200 text-foreground-700 hover:bg-background-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索漫画或标签"
              aria-label="搜索漫画"
              className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <p className="mb-6 text-sm text-foreground-500">
          共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 部{active === '全部' ? '' : active}漫画
        </p>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-100 p-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-400"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的漫画，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((c) => (
              <article
                key={c.id}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-transform hover:-translate-y-1"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={c.cover}
                    alt={`${c.title}漫画封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-2.5 py-1 text-xs text-background-50">
                    {c.status}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                    {c.score}分
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="truncate font-heading text-base font-bold text-foreground-950">{c.title}</h3>
                  </div>
                  <p className="mt-1.5 text-xs text-foreground-600">作者：{c.author}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {c.tags.map((t) => (
                      <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span>{c.category} · {c.chapters}话</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {c.views}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}